import React, { useEffect, useState } from "react";
import { Colors } from "../../../assets/Colors";
import { fetchGet, fetchPut } from "../../../fetchers";
import { useMutation } from "@tanstack/react-query";
import { Card } from "@material-tailwind/react";
import { isRequired, useForm } from "../../../components/isRequired";
import { HrdInputGenerator } from "../../../components/form/HrdInputGenerator";
import "react-toastify/dist/ReactToastify.css";
import { toast } from "react-toastify";
import { BiArrowBack, BiCheckCircle, BiXCircle } from "react-icons/bi";
import { useNavigate, useParams } from "react-router-dom";

// ── Helpers ────────────────────────────────────────────────
const fmt = (val, dec = 0) =>
  parseFloat(val || 0).toLocaleString("id-ID", { maximumFractionDigits: dec });

const fmtDate = (val) => {
  if (!val) return "-";
  const d = new Date(val);
  return d.toLocaleDateString("id-ID", { day: "2-digit", month: "long", year: "numeric" });
};

// ── Confirm Modal ──────────────────────────────────────────
const ConfirmModal = ({ show, type, alasan, setAlasan, onConfirm, onCancel }) => {
  if (!show) return null;
  const isApprove = type === "approve";
  // Alasan wajib saat Tolak — backend menegakkan aturan yang sama dan akan
  // menolak dengan "Alasan penolakan wajib diisi". Dikunci di sini juga supaya
  // pengguna tahu sebelum menekan tombol, bukan lewat pesan error sesudahnya.
  const alasanKosong = !isApprove && !(alasan || "").trim();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm mx-4">
        <div className={`flex items-center gap-3 mb-4 ${isApprove ? "text-green-600" : "text-red-500"}`}>
          {isApprove ? <BiCheckCircle className="text-3xl" /> : <BiXCircle className="text-3xl" />}
          <h3 className="text-lg font-semibold text-gray-800">
            {isApprove ? "Konfirmasi Approve" : "Konfirmasi Tolak"}
          </h3>
        </div>
        <p className="text-gray-600 mb-4 text-sm">
          {isApprove
            ? "Apakah Anda yakin ingin menyetujui pembayaran AP ini?"
            : "Dokumen akan ditutup dan tidak berjurnal. Pengaju harus memperbaiki lalu mengirim ulang."}
        </p>
        {!isApprove && (
          <div className="mb-6">
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Alasan penolakan <span className="text-red-500">*</span>
            </label>
            <textarea
              rows={3}
              value={alasan}
              onChange={(e) => setAlasan(e.target.value)}
              placeholder="Jelaskan apa yang harus diperbaiki pengaju"
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-red-400"
            />
          </div>
        )}
        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Batal
          </button>
          <button
            onClick={onConfirm}
            disabled={alasanKosong}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              isApprove ? "bg-green-500 hover:bg-green-600" : "bg-red-500 hover:bg-red-600"
            }`}
          >
            {isApprove ? "Ya, Approve" : "Ya, Tolak"}
          </button>
        </div>
      </div>
    </div>
  );
};

const SectionTitle = ({ title }) => (
  <div className="flex items-center gap-2 mb-3 mt-5">
    <div className="h-5 w-1 rounded bg-blue-500" />
    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">{title}</h3>
  </div>
);

// ── Main Component ─────────────────────────────────────────
const ApprovalAccountPayable = () => {
  const initialState = { BisnisUnitID: "", Tanggal: new Date(), TanggalCek: new Date() };

  const validations = [
    ({ UserPCName }) => isRequired(UserPCName) || { UserPCName: "Wajib diisi" },
    ({ Status }) => isRequired(Status) || { Status: "Wajib diisi" },
  ];

  const { values, touched, customHandler, changeHandler, errors, blastChange } =
    useForm(initialState, validations);

  const [buttonTouched, setButtonTouched] = useState(false);
  const [detail, setDetail] = useState([]);
  const [jurnal, setJurnal] = useState([]);
  const [confirmModal, setConfirmModal] = useState({ show: false, type: null });
  const [alasanTolak, setAlasanTolak] = useState("");

  const params = useParams();
  const navigate = useNavigate();

  const mutationGet = useMutation((id) => fetchGet("Accounting/PembayaranAP/" + id));
  const mutationUpdate = useMutation((formData) =>
    fetchPut("Accounting/PembayaranAP/approval", formData)
  );

  useEffect(() => {
    if (params.transid) {
      mutationGet.mutate(params.transid.replaceAll("-", "/"));
    }
  }, []);

  useEffect(() => {
    if (mutationUpdate.isSuccess) {
      navigate("/approval-list");
    }
  }, [mutationUpdate.status]);

  useEffect(() => {
    if (mutationGet.data) {
      const d = mutationGet.data.data;
      blastChange({
        ...d,
        JenisCashJournal: d.JenisPembayaran,
        DetailCashJournal: d.DetailPembayaran,
      });
      setDetail(d.Data ?? []);
      setJurnal(d.JurnalPreview ?? []);
    }
  }, [mutationGet.status]);

  const requestAction = (type) => setConfirmModal({ show: true, type });

  const confirmAction = () => {
    const isApprove = confirmModal.type === "approve";
    setConfirmModal({ show: false, type: null });

    let newValues = { ...values };
    try {
      newValues.Nominal = String(newValues.Nominal ?? "");
      newValues.JenisPembayaran = String(newValues.JenisCashJournal ?? "");
      newValues.DetailPembayaran = String(newValues.DetailCashJournal ?? "");
      newValues.NilaiBayar = String(newValues.NilaiBayar ?? "");
      newValues.JenisTransaksi = "AP";
      newValues.KonversiRupiah = String(newValues.KonversiRupiah ?? "0");
      newValues.TanggalCek = newValues.TanggalCek instanceof Date
        ? newValues.TanggalCek.toISOString()
        : newValues.TanggalCek;
      newValues.Tanggal = newValues.Tanggal instanceof Date
        ? newValues.Tanggal.toISOString()
        : newValues.Tanggal;
      newValues.Status = isApprove ? "1" : "2";
      // Dikirim hanya saat Tolak. Backend mewajibkannya untuk Status != "1" dan
      // mengabaikannya saat Approve.
      newValues.KeteranganApproval = isApprove ? "" : alasanTolak.trim();
    } catch (_) {}

    mutationUpdate.mutate(newValues);
  };

  // getDataFromURL - buka/unduh lampiran data pendukung. Sama pola dgn
  // Insert-AccountPayable.js / Kas Bank - approver harus bisa membuka file
  // yang dilampirkan pemohon, bukan cuma melihat namanya.
  const getDataFromURL = (url) => {
    if (!url || url.trim() === "") {
      toast.error("Tidak ada file attachment", { position: "bottom-right", autoClose: 3000 });
      return;
    }
    const encodedUrl = encodeURIComponent(url);
    const fileUrl = `${process.env.REACT_APP_API_UPLOAD_URL}file/${encodedUrl}`;
    fetch(fileUrl, { method: "GET", headers: { Accept: "application/octet-stream" } })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return response.blob();
      })
      .then((blob) => {
        const fileBlobUrl = window.URL.createObjectURL(blob);
        if (blob.type.includes("pdf") || blob.type.includes("image")) {
          window.open(fileBlobUrl, "_blank");
        } else {
          const link = document.createElement("a");
          link.href = fileBlobUrl;
          let downloadName = url;
          if (url.includes("-") && url.split("-")[0].length === 36) {
            downloadName = url.split("-").slice(1).join("-").replace(/_/g, " ");
          }
          link.download = downloadName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          setTimeout(() => URL.revokeObjectURL(fileBlobUrl), 100);
        }
      })
      .catch((err) => {
        console.error("Error loading file:", err);
        toast.error("Gagal membuka file. File mungkin belum diupload ke server.", {
          position: "bottom-right",
          autoClose: 3000,
        });
      });
  };

  const isLoading = mutationGet.isLoading;
  const isSubmitting = mutationUpdate.isLoading;

  return (
    <div className="md:ml-64">
      <ConfirmModal
        show={confirmModal.show}
        type={confirmModal.type}
        alasan={alasanTolak}
        setAlasan={setAlasanTolak}
        onConfirm={confirmAction}
        onCancel={() => {
          setConfirmModal({ show: false, type: null });
          setAlasanTolak("");
        }}
      />

      <div style={{ background: Colors.primary }} className="pt-14 pb-16 px-3 md:px-8 h-auto">
        <div className="container mx-auto max-w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4" />
        </div>
      </div>

      <div className="px-3 md:px-8 h-auto -mt-32">
        <div className="container mx-auto max-w-full">
          <div className="grid grid-cols-1 px-4">
            <Card>
              <div className="flex flex-col mt-5 px-2">

                {/* Header bar */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                  <div>
                    <h2 className="text-lg font-bold text-gray-800">Approval Pembayaran AP</h2>
                    {values.PembayaranAPID && (
                      <p className="text-sm text-gray-500 mt-0.5">ID: {values.PembayaranAPID}</p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => navigate("/approval-list")}
                      disabled={isSubmitting}
                      className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 shadow-sm transition-colors disabled:opacity-50"
                    >
                      <BiArrowBack className="text-base" />
                      Kembali
                    </button>
                    <button
                      onClick={() => requestAction("reject")}
                      disabled={isSubmitting || isLoading}
                      className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg shadow-sm transition-colors disabled:opacity-50"
                    >
                      {isSubmitting && confirmModal.type === "reject" ? (
                        <>
                          <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                          </svg>
                          Memproses...
                        </>
                      ) : (
                        <><BiXCircle className="text-base" /> Tolak</>
                      )}
                    </button>
                    <button
                      onClick={() => requestAction("approve")}
                      disabled={isSubmitting || isLoading}
                      className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-green-500 hover:bg-green-600 rounded-lg shadow-sm transition-colors disabled:opacity-50"
                    >
                      {isSubmitting && confirmModal.type === "approve" ? (
                        <>
                          <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                          </svg>
                          Memproses...
                        </>
                      ) : (
                        <><BiCheckCircle className="text-base" /> Approve</>
                      )}
                    </button>
                  </div>
                </div>

                <hr className="mb-1" />

                {isLoading ? (
                  <div className="flex flex-col gap-3 py-6 animate-pulse">
                    {[...Array(6)].map((_, i) => (
                      <div key={i} className="h-9 bg-gray-100 rounded-md w-full" />
                    ))}
                  </div>
                ) : (
                  <>
                    {/* Sumber Pembayaran */}
                    <SectionTitle title="Sumber Pembayaran" />
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4">
                      <HrdInputGenerator
                        type="text" value={values} name="SumberData" title="Sumber Data"
                        onChange={customHandler} touched={touched} buttonTouched={buttonTouched}
                        size="full" required={true} errors={errors} disabled={true}
                      />
                      <HrdInputGenerator
                        type="text" value={values} name="PurchaseOrderID" title="Purchase Order"
                        onChange={customHandler} touched={touched} buttonTouched={buttonTouched}
                        size="full" required={true} errors={errors} disabled={true}
                      />
                      <HrdInputGenerator
                        type="text" value={values} name="DibayarKepada" title="Dibayar Kepada"
                        onChange={customHandler} touched={touched} buttonTouched={buttonTouched}
                        size="full" required={true} errors={errors} disabled={true}
                      />
                    </div>

                    {/* Detail Pembayaran */}
                    <SectionTitle title="Detail Pembayaran" />
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4">
                      <HrdInputGenerator
                        type="text" value={values} name="JenisCashJournal" title="Jenis Pembayaran"
                        onChange={customHandler} touched={touched} buttonTouched={buttonTouched}
                        size="full" required={true} errors={errors} disabled={true}
                      />
                      <HrdInputGenerator
                        type="DetailPembayaran" value={values} name="DetailCashJournal" title="Detail Pembayaran"
                        onChange={customHandler} touched={touched} buttonTouched={buttonTouched}
                        size="full" required={true} errors={errors} disabled={false}
                      />
                      <HrdInputGenerator
                        type="text" value={values} name="MataUang" title="Mata Uang"
                        onChange={customHandler} touched={touched} buttonTouched={buttonTouched}
                        size="full" required={true} errors={errors} disabled={true}
                      />
                    </div>

                    {/* Tanggal & Cek */}
                    <SectionTitle title="Tanggal & Pembayaran" />
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4">
                      <HrdInputGenerator
                        type="dateOnly" value={values} name="Tanggal" title="Tanggal"
                        onChange={customHandler} touched={touched} buttonTouched={buttonTouched}
                        size="full" required={true} errors={errors} disabled={true}
                      />
                      {values.JenisCashJournal === "Bank" && (
                        <>
                          <HrdInputGenerator
                            type="text" value={values} name="NoCek" title="No. Cek/Bilyet"
                            onChange={changeHandler} touched={touched} buttonTouched={buttonTouched}
                            size="full" required={true} errors={errors} disabled={true}
                          />
                          <HrdInputGenerator
                            type="dateOnly" value={values} name="TanggalCek" title="Tanggal Cek"
                            onChange={changeHandler} touched={touched} buttonTouched={buttonTouched}
                            size="full" required={true} errors={errors} disabled={true}
                          />
                        </>
                      )}
                    </div>

                    {/* Lampiran data pendukung */}
                    <SectionTitle title="Lampiran Data Pendukung" />
                    <div className="mb-4">
                      {values.File && values.File.trim() !== "" ? (
                        <button
                          type="button"
                          onClick={() => getDataFromURL(values.File)}
                          className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 border border-blue-600 rounded shadow-sm transition-colors duration-200"
                        >
                          Lihat / Unduh Lampiran
                        </button>
                      ) : (
                        <span className="text-sm text-gray-400 italic">Tidak ada lampiran</span>
                      )}
                    </div>

                    {/* Keterangan & Nominal */}
                    <SectionTitle title="Keterangan & Nilai" />
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4">
                      <div className="sm:col-span-2">
                        <HrdInputGenerator
                          type="text" value={values} name="Keterangan" title="Keterangan"
                          onChange={changeHandler} touched={touched} buttonTouched={buttonTouched}
                          size="full" required={true} errors={errors} disabled={true}
                        />
                      </div>
                      <HrdInputGenerator
                        type="text" value={values} name="Nominal" title="Nominal"
                        onChange={changeHandler} touched={touched} buttonTouched={buttonTouched}
                        size="full" required={true} errors={errors} disabled={true}
                      />
                      <div className="lg:col-start-3">
                        <HrdInputGenerator
                          type="number" value={values} name="NilaiBayar" title="Nilai Bayar"
                          onChange={changeHandler} touched={touched} buttonTouched={buttonTouched}
                          size="full" required={true} errors={errors} disabled={true}
                        />
                      </div>
                    </div>

                    {/* Detail Journal — preview jurnal yang akan diposting saat approval
                        (Dr Hutang / Cr Uang Muka / Cr Kas-Bank / Dr biaya tambahan). */}
                    <SectionTitle title="Jurnal Akuntansi" />
                    {jurnal.length > 0 ? (
                      (() => {
                        const totDebet = jurnal.reduce((s, r) => s + parseFloat(r.Debet || 0), 0);
                        const totKredit = jurnal.reduce((s, r) => s + parseFloat(r.Kredit || 0), 0);
                        const selisih = totDebet - totKredit;
                        // Baris "Total" menampilkan Nominal (nilai tagihan asli), bukan
                        // jumlah kolom Debet/Kredit — jumlah kolom ikut membesar saat ada
                        // pengembalian kelebihan uang muka ke bank, yang membingungkan
                        // ditampilkan sebagai "total" transaksi ini.
                        const nominalTagihan = parseFloat(values.Nominal || 0);
                        return (
                          <>
                            <div
                              className="-mx-2 mb-2 border-y border-gray-200"
                              style={{ overflowX: "scroll", WebkitOverflowScrolling: "touch" }}
                            >
                              <table className="table-auto text-sm text-left text-gray-600" style={{ minWidth: "500px", width: "100%" }}>
                                <thead className="text-xs text-gray-700 uppercase bg-gray-50 sticky top-0">
                                  <tr>
                                    <th className="py-3 px-4 text-center">No</th>
                                    <th className="py-3 px-4">No. Account</th>
                                    <th className="py-3 px-4">Nama Account</th>
                                    <th className="py-3 px-4">Keterangan</th>
                                    <th className="py-3 px-4 text-right">Debet</th>
                                    <th className="py-3 px-4 text-right">Kredit</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {jurnal.map((item, idx) => {
                                    const rowBase = idx % 2 === 0 ? "bg-white" : "bg-gray-50";
                                    return (
                                      <tr key={idx} className={`${rowBase} border-b hover:bg-blue-50 transition-colors`}>
                                        <td className="py-3 px-4 text-center text-gray-400 text-xs">{idx + 1}</td>
                                        <td className="py-3 px-4 font-mono text-xs whitespace-nowrap">{item.Account ?? "-"}</td>
                                        <td className="py-3 px-4 whitespace-nowrap">{item.AccountName ?? "-"}</td>
                                        <td className="py-3 px-4">{item.Keterangan ?? "-"}</td>
                                        <td className="py-3 px-4 text-right whitespace-nowrap font-medium text-green-700">
                                          {parseFloat(item.Debet || 0) ? fmt(item.Debet) : "-"}
                                        </td>
                                        <td className="py-3 px-4 text-right whitespace-nowrap font-medium text-red-700">
                                          {parseFloat(item.Kredit || 0) ? fmt(item.Kredit) : "-"}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                                <tfoot className="text-xs font-semibold text-gray-700 bg-gray-100 border-t-2 border-gray-300">
                                  <tr>
                                    <td colSpan={4} className="py-3 px-4 text-right">Total</td>
                                    <td className="py-3 px-4 text-right text-green-700">{fmt(nominalTagihan)}</td>
                                    <td className="py-3 px-4 text-right text-red-700">{fmt(nominalTagihan)}</td>
                                  </tr>
                                </tfoot>
                              </table>
                            </div>
                            {selisih !== 0 && (
                              <p className="text-xs text-red-600 mb-4">
                                ⚠ Jurnal tidak seimbang — selisih {fmt(Math.abs(selisih))}.
                              </p>
                            )}
                          </>
                        );
                      })()
                    ) : (
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50 mb-4">
                        <p className="text-sm text-gray-500">Tidak ada data jurnal.</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApprovalAccountPayable;
